import dayjs from 'dayjs';
import { toEnumFromString, fromEnumAsString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry } from '../calendar-entry.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { SerializationContext, DataModelBuilder, isSameData, SerializedData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';

export class RescheduleNotConfirmedEvent extends NotificationEvent {
  static readonly TYPE_ID: string = '54e38df631dc4f8fb0f457b2cb5b8a0b';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly seen: boolean,
    public readonly before: CalendarEntry,
    public readonly after: CalendarEntry,
  ) {
    super();
  }

  get $typeId(): string {
    return RescheduleNotConfirmedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, RescheduleNotConfirmedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): RescheduleNotConfirmedEvent {
    return new RescheduleNotConfirmedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'before')),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'after')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'seen': this.seen,
      'before': context.toData(this.before),
      'after': context.toData(this.after),
    };
  }

  clone(): this {
    return new RescheduleNotConfirmedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.before.clone(),
        this.after.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.seen === this.seen &&
        isSameData(other.before, this.before) &&
        isSameData(other.after, this.after);
  }

  markAsSeen(): this {
    return new RescheduleNotConfirmedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.before.clone(),
        this.after.clone(),
    ) as this;
  }
}

export class RescheduleNotConfirmedEventBuilder extends DataModelBuilder<RescheduleNotConfirmedEvent> {
  build(context: SerializationContext, map: SerializedData): RescheduleNotConfirmedEvent {
    return RescheduleNotConfirmedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(RescheduleNotConfirmedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(RescheduleNotConfirmedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
